// src/hooks/useUserManagement.ts
import { useState, useEffect } from "react";
import { api } from "../services/api";

interface UserData {
  id: string;
  name: string;
  email: string;
  tokenUsage: number;
  tokenLimit: number;
  lastActive: string;
}

interface UseUserManagementReturn {
  users: UserData[];
  loading: boolean;
  error: string | null;
  debug: string;
  formatDate: (dateString: string) => string;
  formatNumber: (num: number) => string;
}

export const useUserManagement = (): UseUserManagementReturn => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debug, setDebug] = useState<string>("");

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "Never") return "ไม่เคยใช้งาน";
    try {
      return new Date(dateString).toLocaleString("th-TH", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("th-TH").format(num);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        setDebug("");

        const response = await api.get("/users", {
          params: {},
        });

        // Debug information
        setDebug((prev) => prev + `\nStatus: ${response.status}`);
        setDebug((prev) => prev + `\nStatusText: ${response.statusText}`);

        const responseData = response.data;
        setDebug(
          (prev) => prev + `\nResponse: ${JSON.stringify(responseData)}`
        );

        if (Array.isArray(responseData)) {
          setUsers(responseData);
          setDebug((prev) => prev + `\nProcessed ${responseData.length} users`);
        } else {
          throw new Error("Received invalid data format from server");
        }
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(
          err instanceof Error
            ? err.message
            : "เกิดข้อผิดพลาดในการดึงข้อมูลผู้ใช้"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  return {
    users,
    loading,
    error,
    debug,
    formatDate,
    formatNumber,
  };
};
